import { computed, observable } from 'mobx';
import type { HotelResponseArgs } from '../hotel/hotel2';
import Hotel, { convertHotelResponseToArgs } from '../hotel/hotel2';
import type { TransitResponseArgs } from '../transit/transit2';
import Transit, { convertTransitResponseToArgs } from '../transit/transit2';
import type { FlightResponseArgs } from '../flight/flight2';
import Flight, { convertFlightResponseToArgs } from '../flight/flight2';
import type Organization from '../organization/organization3';
import type ReservingTrip from './reserving_trip2';
import type MarginType from '../organization/margin_type3';

export type ReservingTripItemResponseArgs = {
  index: number;
  element_type: 'transport' | 'hotel' | 'flight';
  element_id: string;
  domestic_air_price_index: number | null;
  element_raw: TransitResponseArgs | HotelResponseArgs | FlightResponseArgs;
  foreign_exist: boolean;
  hotel_price_limit?: number;
  railway_distance?: number;
};

export type ReservingTripItemArgs = {
  index?: number | string;
  elementType?: 'transport' | 'hotel' | 'flight';
  elementId?: string | number | null;
  domesticAirPriceIndex?: number | null;
  element?: Transit | Hotel | Flight;
  foreignExist: boolean;
  parentTrip?: ReservingTrip;
  hotelPriceLimit?: number;
  railwayDistance?: number;
};

export const convertReservingTripItemResponseToArgs = (response: ReservingTripItemResponseArgs) => {
  const args: ReservingTripItemArgs = {
    index: response.index,
    elementType: response.element_type,
    elementId: response.element_id,
    domesticAirPriceIndex: response.domestic_air_price_index,
    element: (() => {
      switch (response.element_type) {
        case 'transport':
          return new Transit(convertTransitResponseToArgs(response.element_raw as TransitResponseArgs));
        case 'hotel':
          return new Hotel(convertHotelResponseToArgs(response.element_raw as HotelResponseArgs));
        case 'flight':
          return new Flight(convertFlightResponseToArgs(response.element_raw as FlightResponseArgs));
        default:
          throw new Error(`invalid element_type: "${response.element_type}"`);
      }
    })(),
    foreignExist: response.foreign_exist,
    hotelPriceLimit: response.hotel_price_limit,
    railwayDistance: response.railway_distance
  };
  return args;
};

export default class ReservingTripItem {
  @observable index?: number | string;
  @observable elementType?: 'transport' | 'hotel' | 'flight';
  @observable elementId?: string | number | null;
  @observable domesticAirPriceIndex?: number | null;
  @observable element?: Transit | Hotel | Flight;
  @observable foreignExist: boolean;
  @observable parentTrip?: ReservingTrip;
  @observable hotelPriceLimit: number;
  @observable railwayDistance?: number;

  constructor(args?: ReservingTripItemArgs) {
    this.index = args?.index;
    this.elementType = args?.elementType;
    this.elementId = args?.elementId;
    this.domesticAirPriceIndex = args?.domesticAirPriceIndex;
    this.element = args?.element;
    if (this.element) {
      this.element.parentItem = this;
    }

    this.foreignExist = args?.foreignExist ?? false;
    this.parentTrip = args?.parentTrip;
    this.hotelPriceLimit = args?.hotelPriceLimit ?? 0;
    this.railwayDistance = args?.railwayDistance;
  }

  @computed get organization(): Organization | undefined {
    return this.parentTrip?.organization;
  }

  @computed get peopleNum(): number {
    return this.parentTrip?.peopleNum ?? 1;
  }

  @computed get isTransit() {
    return this.elementType === 'transport';
  }

  @computed get isFlight() {
    return this.elementType === 'flight';
  }

  @computed get isHotel() {
    return this.elementType === 'hotel';
  }

  @computed get isDomesticExist() {
    return !this.foreignExist;
  }

  @computed get airExists() {
    if (this.isTransit && this.element instanceof Transit) {
      return this.element.airExists;
    }
    if (this.isFlight) {
      return true;
    }
    if (this.isHotel) {
      return false;
    }

    return undefined;
  }

  @computed get shinkansenExists() {
    if (this.isTransit && this.element instanceof Transit) {
      return this.element.shinkansenExists;
    }

    return false;
  }

  @computed get expressExists() {
    if (this.isTransit && this.element instanceof Transit) {
      return this.element.expressExists;
    }

    return false;
  }

  @computed get totalPrice() {
    if (!this.element) {
      return undefined;
    }
    if (this.element instanceof Transit || this.element instanceof Hotel || this.element instanceof Flight) {
      return this.element.totalPrice;
    }
    return undefined;
  }

  @computed get submitParams() {
    return {
      index: this.index,
      element_type: this.elementType,
      element_id: this.elementId,
      railway_distance: null,
      distance: null,
      route_time: null,
      domestic_air_price_index: this.domesticAirPriceIndex
    };
  }

  @computed get isUnreservedSeatAvailable() {
    if (this.elementType !== 'transport') return false;

    const transit = this.element as Transit;
    return transit.isUnreservedSeatAvailable;
  }

  @computed get startDate(): string | undefined {
    if ((this.isTransit && this.element instanceof Transit) || (this.isFlight && this.element instanceof Flight)) {
      return this.element.startDate;
    }
    if (this.isHotel && this.element instanceof Hotel) {
      return this.element.checkinDateStr;
    }

    return undefined;
  }

  @computed get endDate(): string | undefined {
    if ((this.isTransit && this.element instanceof Transit) || (this.isFlight && this.element instanceof Flight)) {
      return this.element.endDate;
    }
    if (this.isHotel && this.element instanceof Hotel) {
      return this.element.checkoutDateStr;
    }

    return undefined;
  }

  @computed get isHotelPriceExceeded(): boolean {
    if (!(this.element instanceof Hotel)) return false;

    return this.hotelPriceLimit > 0 && (this.element.averagePriceWithTax || 0) > this.hotelPriceLimit;
  }

  @computed get totalBufferAmount(): number {
    if (this.isTransit && this.element instanceof Transit) {
      return this.element.totalBufferAmount;
    }
    if (this.isHotel && this.element instanceof Hotel) {
      return this.element.totalBufferAmount;
    }
    if (this.isFlight && this.element instanceof Flight) {
      return this.element.totalBufferAmount;
    }
    return 0;
  }

  @computed get showFee(): boolean {
    return this.parentTrip?.showFee ?? true;
  }

  @computed get marginTypes(): MarginType[] {
    return this.parentTrip?.marginTypes ?? [];
  }

  @computed get marginType(): MarginType | undefined {
    if (this.isTransit && this.element instanceof Transit) {
      return this.element.shinkansen
        ? this.marginTypes.find(m => m.category === 'domestic_shinkansen')
        : this.marginTypes.find(m => m.category === 'domestic_flight');
    }
    if (this.isFlight) {
      return this.marginTypes.find(m => m.category === 'foreign');
    }
    if (this.isHotel && this.element instanceof Hotel) {
      return this.foreignExist
        ? this.marginTypes.find(m => m.category === 'foreign_hotel')
        : this.marginTypes.find(m => m.category === 'domestic_hotel');
    }
    return undefined;
  }

  @computed get deadlineHour(): number | undefined {
    if (this.isFlight && this.element instanceof Flight && this.element.itineraries[0].timeToLive) {
      return this.element.itineraries[0].timeToLive;
    }
    return undefined;
  }

  @computed get priceText(): string | undefined {
    if (this.isTransit && this.element instanceof Transit) {
      return this.element.transitPriceText;
    }
    if (this.isFlight && this.element instanceof Flight) {
      return this.element.priceText;
    }
    if (this.isHotel && this.element instanceof Hotel) {
      return this.element.priceDetailText;
    }

    return undefined;
  }
}
